/**
 * Enhancement levels — every beneficial blueprint grows stronger each time
 * it is drafted in a run, up to MAX_ENHANCEMENT_LEVEL (Mk. V). The Mk. V
 * step is a deliberate spike: roughly double the Mk. IV magnitude.
 *
 * `GameState.enhancementLevels` tracks picks-per-type for the run. A draft
 * card offers level `picks + 1`, capped at Mk. V. A blueprint that has
 * reached Mk. V stays in the draft pool and is re-offered at Mk. V, so the
 * ultimate form can be taken again (re-arming / stacking its effect).
 *
 * MIRRORED in supabase/functions/_shared/engine-replay.ts — keep in sync.
 */

import { MutationType } from './registry.js';

export const MAX_ENHANCEMENT_LEVEL = 5;

/** Sentinel magnitude for Cascade Coil Mk. V: clear every touched color. */
export const CASCADE_COIL_CLEAR_ALL = -1;

/** Primary magnitude per level (index 0 = Mk. I). */
const LEVEL_TABLES: Partial<Record<MutationType, readonly [number, number, number, number, number]>> = {
  /** Blast radius in hex rings. */
  [MutationType.BubbleBomb]: [1, 2, 3, 4, 8],
  /** Turns of doubled base points. */
  [MutationType.ScoreFrenzy]: [5, 6, 7, 8, 16],
  /** Turns the ceiling is frozen. */
  [MutationType.SlowClockwork]: [5, 6, 7, 8, 16],
  /** Turns of 2.5x bank bonus. */
  [MutationType.LubricatedBearings]: [5, 6, 7, 8, 16],
  /** Turns the multiplier cannot decay. */
  [MutationType.GovernorLock]: [5, 6, 7, 8, 16],
  /** Shots drawn from the narrowed palette. */
  [MutationType.NarrowSpectrum]: [5, 6, 7, 8, 16],
  /** Gears shattered on pick. */
  [MutationType.RainbowRow]: [3, 4, 5, 6, 12],
  /** Wild shots granted. */
  [MutationType.WildcardGear]: [1, 2, 3, 4, 8],
  /** Random gears removed per touched color on landing (Mk. V: all). */
  [MutationType.CascadeCoil]: [3, 5, 7, 9, CASCADE_COIL_CLEAR_ALL],
};

/** Clamp an arbitrary level into the valid Mk. I..V range. */
export function clampLevel(level: number | undefined): number {
  if (level === undefined || !Number.isFinite(level)) return 1;
  return Math.max(1, Math.min(MAX_ENHANCEMENT_LEVEL, Math.floor(level)));
}

/**
 * Primary magnitude of an enhancement at a given level. Types without a
 * ladder (harmful cards) return 0.
 */
export function enhancementMagnitude(type: string, level: number | undefined): number {
  const table = LEVEL_TABLES[type as MutationType];
  if (!table) return 0;
  return table[clampLevel(level) - 1]!;
}

/** True when the blueprint has been drafted MAX_ENHANCEMENT_LEVEL times. */
export function isEnhancementMaxed(levels: Readonly<Record<string, number>>, type: string): boolean {
  return (levels[type] ?? 0) >= MAX_ENHANCEMENT_LEVEL;
}

/** The level a draft of `type` would grant given the run's pick counts. */
export function nextEnhancementLevel(levels: Readonly<Record<string, number>>, type: string): number {
  return clampLevel((levels[type] ?? 0) + 1);
}

/** Roman numerals for the Mk. label. */
export const LEVEL_NUMERALS: readonly string[] = ['I', 'II', 'III', 'IV', 'V'];

export function levelLabel(level: number | undefined): string {
  return `Mk. ${LEVEL_NUMERALS[clampLevel(level) - 1]}`;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/**
 * Level-aware rules text for a beneficial card. Harmful cards fall back to
 * the caller's static description.
 */
export function describeEnhancement(type: string, level: number | undefined, fallback: string): string {
  const lvl = clampLevel(level);
  const n = enhancementMagnitude(type, lvl);
  switch (type as MutationType) {
    case MutationType.BubbleBomb:
      return `Next shot is a powder keg with a blast radius of ${n}.`;
    case MutationType.ScoreFrenzy:
      return `Base points doubled for ${plural(n, 'turn')}.`;
    case MutationType.SlowClockwork:
      return `The ceiling stops descending for ${plural(n, 'turn')}.`;
    case MutationType.LubricatedBearings:
      return `Wall bounces pay 2.5x instead of 1.5x for ${plural(n, 'turn')}.`;
    case MutationType.GovernorLock:
      return `Misses don't drain your multiplier for ${plural(n, 'turn')}.`;
    case MutationType.NarrowSpectrum:
      return `Your next ${plural(n, 'shot')} come in only 3 colors.`;
    case MutationType.RainbowRow:
      return `Shatters ${plural(n, 'random gear')}. Anything left hanging falls.`;
    case MutationType.WildcardGear:
      return n === 1
        ? 'Next shot is a wild gear that matches any color.'
        : `Next ${n} shots are wild gears that match any color.`;
    case MutationType.CascadeCoil:
      return n === CASCADE_COIL_CLEAR_ALL
        ? 'Next shot is a spark: every gear of each color it touches falls, board-wide.'
        : `Next shot is a spark: ${n} gears of each color it touches fall, board-wide.`;
    default:
      return fallback;
  }
}
